// Updated AccountInfo Component
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useVendorForm } from "../VendorFormContext";
import { formatPhone, mapToOption, formatDateCST, formatTimeCST } from "../../../../utlis";
import Pencil from "../../../../images/pencil.png";
import { getVendorDetailsById } from "../../../../api/sales";
import Loader from "../../../../components/Loader";

// Format phone number as user types (live formatting)
const formatPhoneInput = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
};

const AccountInfo = () => {
  // Add missing onSubmit function to fix ESLint error
  const onSubmit = (data) => {
    saveVendorData("accountInfo", data);
    toast.success("Vendor data saved successfully.");
    reset(data); // Reset with current data to clear dirty state
  };
  const navigate = useNavigate();
  const { formData, updateFormData, saveVendorData, saveAllChanges, resetVendorData, isCreating, isSaving, hasUnsavedChanges, markTabComplete } = useVendorForm();
  const [loading, setLoading] = useState(false);
  const [auditInfo, setAuditInfo] = useState({});
  // Try to get vendorId from Redux first, fallback to location.state for backward compatibility
  const selectedVendor = useSelector((state) => state.vendor.selectedVendor);
  const location = useLocation();
  const rawVendorId = selectedVendor?.vendor_id || location.state?.vendorId;
  const vendorId = rawVendorId && !isNaN(parseInt(rawVendorId, 10)) ? parseInt(rawVendorId, 10) : null;

  const defaultValues = formData?.accountInfo || {};
  const {
    register,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({ defaultValues });

  const referenceData = useSelector((state) => state.reference);
  const watchedData = watch();
  const prevWatchedData = useRef(watchedData);
  const isFirstRender = useRef(true);

  // Auto-sync form changes to context for global change detection
  // Skip the first sync — resetVendorData already populated the context with correct data
  useEffect(() => {
    if (JSON.stringify(prevWatchedData.current) !== JSON.stringify(watchedData)) {
      if (!isFirstRender.current) {
        updateFormData("accountInfo", watchedData);
      }
      prevWatchedData.current = watchedData;
      isFirstRender.current = false;
    }
  }, [watchedData, updateFormData]);

  // Fetch vendor details if vendorId exists
  useEffect(() => {
    const fetchVendorDetails = async () => {
      if (vendorId && (!formData.accountInfo || Object.keys(formData.accountInfo).length === 0)) {
        setLoading(true);
        try {
          const response = await getVendorDetailsById(vendorId);
          const vendorData = response.data?.response;
          const accountData = {
            name: vendorData?.name,
            type: vendorData?.type,
            phoneCountry: vendorData?.phoneCountry,
            phone: formatPhoneInput(vendorData?.phone || ''),
            faxCountry: vendorData?.faxCountry,
            fax: formatPhone(vendorData?.fax),
            website: vendorData?.website,
            status: vendorData?.status,
            account_type: vendorData?.account_type,
            annual_revenue: vendorData?.annual_revenue,
          };
          // Audit fields stored separately — they're read-only and should not affect change detection
          setAuditInfo({
            created: vendorData?.created || '',
            created_by: vendorData?.created_by || '',
            updated: vendorData?.updated || '',
            updated_by: vendorData?.updated_by || '',
          });
          const billData = vendorData.bill_to_address;
          const companyProfile = {
            industry: vendorData?.industry,
            yearStarted: vendorData?.year_started,
            totalEmployees: vendorData?.total_employees,
            DUNS: vendorData?.DUNS,
            NAICS: vendorData?.NAICS,
            ownership: vendorData?.ownership,
          };
          const companyPreference = {
            contactMethod: vendorData?.contact_method,
            email: vendorData?.email,
            followupEmail: vendorData?.followup_email,
            bulkEmail: vendorData?.bulk_email,
          };
          const billingTerms = {
            currency: vendorData?.currency,
            creditLimit: vendorData?.credit_limit,
            creditHold: vendorData?.credit_hold,
          };
          const description = {
            description: vendorData?.description,
          };
          // Transform contacts to include mobileNum1 and isPrimary upfront
          // so formData and serverFormData match from the start
          const rawContacts = vendorData?.contacts || [];
          const contacts = Array.isArray(rawContacts) ? rawContacts.map((c, index) => ({
            ...c,
            firstName: c.firstName || c.first_name || '',
            lastName: c.lastName || c.last_name || '',
            mobileNum1: c.mobileNum1 || c.phone?.mobile || c.phone?.phone || (typeof c.phone === 'string' ? c.phone : '') || '',
            isPrimary: c.isPrimary || c.contact_type === 'Primary' || index === 0,
          })) : [];
          resetVendorData({
            accountInfo: accountData,
            billingInfo: billData,
            companyProfile: companyProfile,
            billingTerms: billingTerms,
            companyPreference: companyPreference,
            description: description,
            contacts: contacts,
          });
          reset(accountData); // Populate form fields
        } catch (error) {
          toast.error("Failed to fetch vendor details. Please try again.");
        }
        setLoading(false);
      }
    };
    fetchVendorDetails();
  }, [vendorId, formData.accountInfo, resetVendorData, reset]);

  return (
    <div className="relative w-full min-h-[300px]">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-40 z-10">
          <Loader />
        </div>
      )}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-y-6 mt-5 text-sm font-normal text-text-hint"
      >
        {/* Wrap all form content in a single parent div to avoid adjacent JSX errors */}
        <div className="flex flex-col gap-y-6">
          <div className="grid grid-cols-4 gap-3">
            <div className="">
              <label className="">Vendor Name<span className="text-danger-500">*</span></label>
              <div className={`flex justify-center items-center px-3 py-2 mt-1 border rounded-lg ${errors.name ? "border-danger-500" : "border-neutral-500"}`}>
                <input
                  {...register("name", {
                    required: "Vendor name is required",
                    maxLength: { value: 25, message: "Vendor name must not exceed 25 characters" },
                    validate: (value) => value?.trim() !== "" || "Vendor name cannot be blank"
                  })}
                  placeholder="Vendor Name"
                  maxLength={25}
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
              {errors.name && <span className="text-danger-500">{errors.name.message}</span>}
            </div>
            <div className="">
              <label className="">Phone Number<span className="text-danger-500">*</span></label>
              <div className={`flex justify-center items-center px-3 py-2 mt-1 border rounded-lg ${errors.phone ? "border-danger-500" : "border-neutral-500"}`}>
                <input
                  {...register("phone", {
                    required: "Phone number is required",
                    pattern: {
                      value: /^\(\d{3}\)\s\d{3}\s\d{4}$/,
                      message: "Please enter a valid phone number (999) 999 9999"
                    }
                  })}
                  placeholder="(999) 999 9999"
                  maxLength={14}
                  onChange={(e) => setValue("phone", formatPhoneInput(e.target.value))}
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
              {errors.phone && <span className="text-danger-500">{errors.phone.message}</span>}
            </div>
            <div className="">
              <label className="">Fax</label>
              <div className={`flex justify-center items-center px-3 py-2 mt-1 border rounded-lg ${errors.fax ? "border-danger-500" : "border-neutral-500"}`}>
                <input
                  {...register("fax", {
                    pattern: {
                      value: /^$|^\(\d{3}\)\s\d{3}\s\d{4}$/,
                      message: "Please enter a valid fax number (999) 999 9999"
                    }
                  })}
                  placeholder="(999) 999 9999"
                  maxLength={14}
                  onChange={(e) => setValue("fax", formatPhoneInput(e.target.value))}
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
              {errors.fax && <span className="text-danger-500">{errors.fax.message}</span>}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="">
              <label className="">Vendor Type<span className="text-danger-500">*</span></label>
              <select
                {...register("type", { required: "Vendor type is required" })}
                className={`flex justify-center items-center focus:ring-0 px-3 py-2 mt-1 border rounded-lg outline-none w-full text-black ${errors.type ? "border-danger-500" : "border-neutral-500"}`}
              >
                {mapToOption(referenceData.vendor?.vendor_types, "type").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
              {errors.type && <span className="text-danger-500">{errors.type.message}</span>}
            </div>
            <div className="">
              <label className="">Account Type<span className="text-danger-500">*</span></label>
              <select
                {...register("account_type", { required: "Account type is required" })}
                className={`flex justify-center items-center focus:ring-0 px-3 py-2 mt-1 border rounded-lg outline-none w-full text-black ${errors.account_type ? "border-danger-500" : "border-neutral-500"}`}
              >
                {mapToOption(referenceData.vendor?.account_types, "type").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
              {errors.account_type && <span className="text-danger-500">{errors.account_type.message}</span>}
            </div>
            <div className="">
              <label className="">Annual Revenue</label>
              <div className={`flex justify-center items-center px-3 py-2 mt-1 border rounded-lg ${errors.annual_revenue ? "border-danger-500" : "border-neutral-500"}`}>
                <input
                  {...register("annual_revenue", {
                    pattern: {
                      value: /^\d+$/,
                      message: "Annual revenue must be a whole number"
                    },
                    setValueAs: (value) => value === '' ? '' : parseInt(value, 10)
                  })}
                  placeholder="Annual Revenue"
                  inputMode="numeric"
                  className="outline-none w-full text-black"
                  onKeyDown={(e) => {
                    // Allow only digits, backspace, delete, tab, arrows
                    if (!/[\d]/.test(e.key) &&
                        !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key) &&
                        !(e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                    }
                  }}
                />
                <img src={Pencil} alt="pencil" />
              </div>
              {errors.annual_revenue && <span className="text-danger-500">{errors.annual_revenue.message}</span>}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="">
              <label className="">Website</label>
              <div className={`flex justify-center items-center px-3 py-2 mt-1 border rounded-lg ${errors.website ? "border-danger-500" : "border-neutral-500"}`}>
                <input
                  {...register("website", {
                    maxLength: { value: 50, message: "Website must not exceed 50 characters" },
                  })}
                  placeholder="Website"
                  maxLength={50}
                  className="outline-none w-full text-black"
                />
                <img src={Pencil} alt="pencil" />
              </div>
              {errors.website && <span className="text-danger-500">{errors.website.message}</span>}
            </div>
            <div className="">
              <label className="">Status<span className="text-danger-500">*</span></label>
              <select
                {...register("status", { required: "Status is required" })}
                className={`flex justify-center items-center focus:ring-0 px-3 py-2 mt-1 border rounded-lg outline-none w-full text-black ${errors.status ? "border-danger-500" : "border-neutral-500"}`}
              >
                {mapToOption(referenceData.genericstatuses?.status, "value").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
              {errors.status && <span className="text-danger-500">{errors.status.message}</span>}
            </div>
          </div>
          {/* Audit Fields - Only show in Edit Mode */}
          {vendorId && !isCreating && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Created By</label>
                <p className="text-sm text-gray-600">{auditInfo?.created_by || 'N/A'}</p>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Date Created</label>
                <p className="text-sm text-gray-600">
                  {auditInfo?.created ? formatDateCST(auditInfo.created) : 'N/A'}
                </p>
                {auditInfo?.created && (
                  <p className="text-xs text-gray-500">{formatTimeCST(auditInfo.created)}</p>
                )}
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Updated By</label>
                <p className="text-sm text-gray-600">{auditInfo?.updated_by || 'N/A'}</p>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-text-hint text-xs">Last Updated</label>
                <p className="text-sm text-gray-600">
                  {auditInfo?.updated ? formatDateCST(auditInfo.updated) : 'N/A'}
                </p>
                {auditInfo?.updated && (
                  <p className="text-xs text-gray-500">{formatTimeCST(auditInfo.updated)}</p>
                )}
              </div>
            </div>
          )}

          <div className="w-full py-10 px-8 flex justify-end items-center gap-2">
            {!isCreating && hasUnsavedChanges && (
              <button
                type="button"
                disabled={isSaving}
                onClick={saveAllChanges}
                className="text-white bg-green-600 py-2 px-5 rounded-full text-base hover:bg-green-700 transition disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit((data) => {
                updateFormData("accountInfo", data);
                if (isCreating) markTabComplete("account-info");
                navigate("/sales/vendors/summary/contact");
              })}
              className="text-white bg-secondary-700 py-2 px-5 rounded-full text-base"
            >
              Next
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AccountInfo;